import React, { useEffect } from "react";
import { size } from "lodash";
import {
  CardHeader,
  CardContent,
  Card,
  Icon,
  Button,
  Loader,
  Dimmer,
  ButtonContent,
  Label,
} from "semantic-ui-react";
import {
  Sev01,
  Sev02,
  Sev03,
  Smu01,
  Smu02,
  Smu03,
  Smu04,
  Smu05,
  Smu06,
  Smu07,
  Smu08,
} from "../TemplatesManager";
import "./SessionContent.scss";

export function SessionContent(props) {
  const {
    activeSession,
    indexActiveSlide,
    countdown,
    setCountdown,
    handleNextSlide,
    handlePrevSlide,
    isLoading,
    isMobile,
    toggleSidebar,
  } = props;

  /**
   * Manages a countdown timer within a component.
   * 
   * This useEffect hook is designed to manage a countdown timer by decrementing the `countdown` state
   * by one every second, until it reaches zero. It first checks if the `countdown` value is an integer,
   * to ensure the countdown is only run with valid numerical values. If the `countdown` is a valid integer,
   * it sets up an interval that decrements the `countdown` state by one every 1000 milliseconds (1 second).
   * 
   * The interval is cleared and reset every time the `countdown` state changes, ensuring that the countdown
   * accurately reflects any updates to the `countdown` state. When the countdown reaches zero, the interval
   * stops decrementing the state, preventing it from going into negative values.
   * 
   * This hook also properly cleans up the interval when the component unmounts or the countdown state changes,
   * to prevent memory leaks and ensure that the interval does not continue to run unnecessarily.
   * 
   * @dependency countdown - The current value of the countdown timer, managed as a state in the component.
   */
  useEffect(() => {
    if (Number.isInteger(countdown)) {
      const timer = setInterval(() => {
        setCountdown(prevCountdown => prevCountdown > 0 ? prevCountdown - 1 : 0);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown]);

  /**
   * Formats a countdown time into a mm:ss string format.
   * 
   * This function takes a countdown time in seconds and formats it into a string
   * representation showing minutes and seconds in mm:ss format. If the input is null
   * or not a number, it returns a placeholder string "--:--". This is useful for displaying
   * countdown timers or time left in a user-friendly format.
   * 
   * The function calculates minutes by dividing the total seconds by 60 and rounding down
   * to the nearest whole number. Seconds are obtained by taking the remainder of the total
   * seconds divided by 60. Both minutes and seconds are converted to strings and padded with
   * leading zeros if necessary to ensure they are always displayed as two digits.
   * 
   * @param {number|null} countdown - The countdown time in seconds.
   * @returns {string} The formatted time as a string in mm:ss format or "--:--" if input is invalid.
   */
  const formatTime = (countdown) => {
    if (countdown === null || isNaN(countdown)) return "--:--"; // Maneja el caso cuando timeLeft es null
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  /**
   * Converts a numerical order into a lowercase letter.
   * 
   * This function takes a numerical order as its argument and returns the corresponding
   * lowercase letter of the English alphabet. If the order is less than 1 or greater than 26,
   * it returns null, indicating an invalid input. The function calculates the ASCII code of the
   * letter by adding the order to 96 (the ASCII code for 'a' is 97). It then converts this ASCII
   * code back to a character using String.fromCharCode.
   * 
   * @param {number} order - The numerical order of the letter in the alphabet (1 for 'a', 2 for 'b', etc.).
   * @returns {string|null} The corresponding lowercase letter of the alphabet, or null if the input is out of range.
   */
  const onLetterByOrder = (order) => {
    if (order < 1 || order > 26) return null;
    return String.fromCharCode(96 + order);
  };

  /**
   * Shuffles the elements of an array in place.
   * 
   * This function implements the Fisher-Yates (aka Knuth) shuffle algorithm to randomly shuffle
   * the elements of an array. It iterates over the array from the last element to the first, 
   * selecting a random element from the unshuffled portion of the array (including the current element)
   * and then swapping it with the current element. This process ensures each element has an equal
   * probability of ending up in any position within the array.
   * 
   * @param {Array} array - The array to be shuffled.
   * @returns {Array} The same array passed as input, but with its elements shuffled.
   */
  const shuffleArray = (array) => {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
  }

  /**
   * Renders a slide based on its template type.
   * 
   * This function takes a slide object and its index in the array as arguments.
   * It then checks the template type of the slide and renders the corresponding
   * component. Each slide component is passed a set of common props including
   * a unique key (index), the slide index, the slide ID, the active session,
   * and a function for handling letter ordering. Some slide types also receive
   * a shuffleArray function for additional functionality.
   * 
   * @param {Object} slide - The slide object containing information about the slide to be rendered.
   * @param {number} index - The index of the slide in the array of slides.
   * @returns A slide component based on the template type of the slide or null if the template type is unrecognized.
   * 
   * Supported slide template types and their corresponding components:
   * - Sev01: Renders <Sev01 /> component.
   * - Sev02: Renders <Sev02 /> component.
   * - Sev03: Renders <Sev03 /> component.
   * - Smu01 to Smu08: Renders <Smu01 /> to <Smu08 /> components respectively.
   * 
   * Each component is rendered with the following props:
   * - key: A unique key for React's rendering algorithm, set to the slide's index.
   * - index: The index of the slide.
   * - slideId: The unique identifier of the slide.
   * - activeSession: Information about the active session.
   * - onLetterByOrder: A function to handle letter ordering.
   * - shuffleArray (optional): A function provided to certain slide types for shuffling elements.
   */
  const renderSlide = (slide, index) => {
    switch (slide.template) {
      case "Sev01":
        return (
          <Sev01
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
            shuffleArray={shuffleArray}
          />
        );
      case "Sev02":
        return (
          <Sev02
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
            shuffleArray={shuffleArray}
          />
        );
      case "Sev03":
        return (
          <Sev03
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
            shuffleArray={shuffleArray}
          />
        );
      case "Smu01":
        return (
          <Smu01
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu02":
        return (
          <Smu02
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu03":
        return (
          <Smu03
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu04":
        return (
          <Smu04
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu05":
        return (
          <Smu05
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu06":
        return (
          <Smu06
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu07":
        return (
          <Smu07
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu08":
        return (
          <Smu08
            key={index}
            index={index}
            slideId={slide._id}
            activeSession={activeSession}
            onLetterByOrder={onLetterByOrder}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="session-content">
      <Card fluid>
        <CardContent className="content-header">
          <CardHeader>
            <CardHeader>
              <div className="session-title">
                {isMobile ? (
                  <Button
                    color="blue"
                    compact
                    icon="list alternate"
                    className="open-sidebar"
                    onClick={toggleSidebar}
                    size="tiny"
                  />
                ) : (
                  <Icon className="session-content__icon" name="caret square right outline" />
                )}
                Contenido de sesión: {activeSession?.title}
              </div>
            </CardHeader>
          </CardHeader>
        </CardContent>
        <CardContent className="content-body">
          {isLoading ? (
            <Dimmer active inverted>
              <Loader content="Cargando contenido de sesión" />
            </Dimmer>
          ) : activeSession?.id && activeSession.slides.length >= 0 ? (
            renderSlide(activeSession.slides[indexActiveSlide], indexActiveSlide)
          ) : (
            <h3>
              <Icon name="hand point left outline" />
              Selecciona una sesión
            </h3>
          )}
        </CardContent>
        {activeSession?.id && size(activeSession.slides) !== 0 ? (
          <CardContent className="content-footer" >
            <Button
              size="large"
              color="blue"
              disabled={indexActiveSlide + 1 <= 1}
              onClick={() => handlePrevSlide()}
              animated
            >
              <ButtonContent visible>Anterior</ButtonContent>
              <ButtonContent hidden>
                <Icon name="arrow left" />
              </ButtonContent>
            </Button>
            <div className="content-footer-pagination">
              <Button
                size="large"
                color='black'
                onClick={null}>
                Página {indexActiveSlide + 1} de {activeSession.slides.length}
              </Button>
            </div>
            <div>
              <Button
                size="large"
                color="blue"
                disabled={indexActiveSlide + 1 >= activeSession.slides.length}
                onClick={() => handleNextSlide()}
                animated
              >
                <ButtonContent visible>Siguiente</ButtonContent>
                <ButtonContent hidden>
                  <Icon name="arrow right" />
                </ButtonContent>
              </Button>
            </div>
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
}
